import React, { Fragment, ReactNode, useEffect, useState } from 'react'
import {
  Modal,
  ModalScrollableContent,
  fillFormStyle
} from '../../../components/_EOS/Modal'
import { Size } from '../../../hooks/useModal'
import {
  patchProductOriginOperational,
  getTypeOperationWarehouse,
  ProductOriginOperationalPayload
} from '../api'
import { useProductOrigins, useWarehouses } from '../../../hooks'
import {
  ProductOrigin,
  SelectedWarehousesByOperation
} from '../../../interfaces/Producto/ProductOrigins'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  FormControlLabel,
  GlobalStyles,
  Grid,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Switch,
  Typography,
  useTheme
} from '@mui/material'
import DescriptionOutlined from '@mui/icons-material/DescriptionOutlined'
import SettingsOutlined from '@mui/icons-material/SettingsOutlined'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined'
import PrecisionManufacturingIcon from '@mui/icons-material/PrecisionManufacturing'
import LockOutlined from '@mui/icons-material/LockOutlined'
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined'
import BalanceOutlined from '@mui/icons-material/BalanceOutlined'
import { useSettings } from '../../../hooks/useSettings'
import { eSetting } from '../../../enums/eSetting'
import { eTypeSetting } from '../../../enums/eTypeSetting'
import { eTypeStorage } from '../../../enums/eTypeStorage'
import { productOriginValidationSchema } from '../Config/config'
import { useApi } from '../../../hooks/useApi'
import { Status } from '../../../hooks/useFetchState'
import { TypeOperationWarehousesTable } from '../../../components/TypeOperation/TypeOperationWarehouse'
import { TypeOperation } from '../../../interfaces/TypeOperation/TypeOperation'
import { YesNoBadge } from '../../../components/YesNoBadge'
import { renderAllRelations, RequiredMark } from '../Components'
import CustomAlert from '../../../components/_WMS/AlertMessage/AlertMessage'
import { WarehouseOutlined } from '@mui/icons-material'

interface ProductOriginModalProps {
  isOpen: boolean
  size: Size
  extendedProps?: ProductOrigin
  closeModal: () => void
  handleCloseModalMessage: (succes: boolean, message: string) => void
}

const SectionTitle: React.FC<{ icon: ReactNode; children: ReactNode }> = ({
  icon,
  children
}) => (
  <Box display='flex' alignItems='center' gap={0.75} mb={0.75}>
    {icon}
    <Typography variant='subtitle1'>{children}</Typography>
  </Box>
)

const LockedField: React.FC<{ label: string; children: ReactNode }> = ({
  label,
  children
}) => (
  <Box
    display='flex'
    justifyContent='space-between'
    alignItems='flex-start'
    sx={{ bgcolor: 'grey.100', borderRadius: 1, px: 1, py: 0.5 }}
  >
    <Box>
      <Typography variant='caption' color='textSecondary' display='block'>
        {label}
      </Typography>
      <Box mt={0.25}>{children}</Box>
    </Box>
    <LockOutlined fontSize='small' color='disabled' />
  </Box>
)

const originAlertMessageClass = 'product-origin-alert-message'
const originAlertIconClass = 'product-origin-alert-icon'

export const ProductOriginModal: React.FC<ProductOriginModalProps> = ({
  isOpen,
  closeModal,
  size,
  extendedProps,
  handleCloseModalMessage
}) => {
  const theme = useTheme()

  const {
    callApi: patchOriginCallApi,
    state: { status }
  } = useApi()

  const { callApi: getTypeOperationCallApi, state: stateTypeOperation } =
    useApi<TypeOperation[]>()

  const { getProductOrigins } = useProductOrigins()

  const { settingsState, getSettings } = useSettings()

  const {
    getWarehouses,
    warehousesState: { warehouses }
  } = useWarehouses()

  useEffect(() => {
    if (warehouses?.length === 0) {
      getWarehouses()
    }
  }, [])

  const [overloadPercentage, setOverloadPercentage] = useState(0)

  const [selectedWarehousesByOperation, setSelectedWarehousesByOperation] =
    useState<SelectedWarehousesByOperation[]>()

  const requestGetTypeOperation = (
    typeStorageId: number,
    productOriginId?: number
  ) => {
    getTypeOperationCallApi(
      getTypeOperationWarehouse(typeStorageId, productOriginId)
    )
  }

  useEffect(() => {
    if (isOpen && extendedProps?.productOriginId) {
      getSettings(eTypeSetting.Automatizaciones, extendedProps.productOriginId)
      setOverloadPercentage(
        Math.max(0, Math.min(100, extendedProps.overloadPercentage))
      )
      requestGetTypeOperation(
        extendedProps.typeStorageId,
        extendedProps.productOriginId
      )
    } else if (!isOpen) {
      setSelectedWarehousesByOperation([])
    }
  }, [isOpen, extendedProps])

  useEffect(() => {
    if (stateTypeOperation.data && stateTypeOperation.data.length > 0) {
      setSelectedWarehousesByOperation(
        stateTypeOperation.data.map((op) => ({
          typeOperationId: op.typeOperationId,
          warehouseIds: op.warehouses.map((w) => w.warehouseId)
        }))
      )
    }
  }, [stateTypeOperation.data])

  const requestProductOriginPatch = (
    productOriginId: number,
    payload: ProductOriginOperationalPayload
  ) => {
    patchOriginCallApi(patchProductOriginOperational(productOriginId, payload))
      .then(() => {
        getProductOrigins()
        handleCloseModalMessage(
          true,
          `Marca actualizada correctamente para marca ${payload.name}`
        )
      })
      .catch((error) => {
        handleCloseModalMessage(false, error)
        throw error
      })
  }

  const handleSetData = (values: ProductOrigin) => {
    const payload: ProductOriginOperationalPayload = {
      name: values.name,
      hasExpirationDays: values.hasExpirationDays || false,
      expirationDays: values.hasExpirationDays ? values.expirationDays : null,
      typeStorageId: values.typeStorageId,
      warehousesByOperation: selectedWarehousesByOperation || [],
      hasPriorityCourier: values.hasPriorityCourier || false,
      hasPrioritySingleSku: values.hasPrioritySingleSku || false,
      hasPriorityOverload: values.hasPriorityOverload || false,
      overloadPercentage: values.hasPriorityOverload ? overloadPercentage : null
    }

    return requestProductOriginPatch(values.productOriginId, payload)
  }

  const handleInputChangeNumericNotNegative = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (['e', 'E', '+', '-', '.'].includes(e.key)) {
      e.preventDefault()
    }
  }

  const handleSelectedWarehouses = (
    typeOperationId: number,
    newSelected: number[]
  ) => {
    setSelectedWarehousesByOperation((prev) => {
      if (!prev) return [{ typeOperationId, warehouseIds: newSelected }]

      const exists = prev.find((op) => op.typeOperationId === typeOperationId)

      if (exists) {
        return prev.map((op) =>
          op.typeOperationId === typeOperationId
            ? { ...op, warehouseIds: newSelected }
            : op
        )
      } else {
        return [...prev, { typeOperationId, warehouseIds: newSelected }]
      }
    })
  }

  const isDisableButton = (isSubmitting: boolean, isValid: boolean) => {
    return (
      isSubmitting ||
      !isValid ||
      stateTypeOperation.status === Status.Loading ||
      status === Status.Loading
    )
  }

  if (!extendedProps) return null

  return (
    <Modal
      loading={status === Status.Loading}
      open={isOpen}
      title='Editar Marca'
      onClose={closeModal}
      maxWidth={size}
      compact
      fillHeight='content'
    >
      <Fragment>
        <GlobalStyles
          styles={{
            [`.${originAlertMessageClass}.${originAlertMessageClass}`]: {
              fontSize: '0.8rem',
              fontWeight: 700
            },
            [`.${originAlertIconClass}.${originAlertIconClass}`]: {
              fontSize: 19,
              marginRight: 6
            }
          }}
        />
        {/* Contenido fijo arriba del area con scroll: no cede alto cuando el
            formulario pide crecer. */}
        <Grid
          container
          spacing={1}
          mb={1}
          alignItems='center'
          sx={{ flexShrink: 0, pr: 0.5 }}
        >
          <Grid item xs={12} md={6}>
            <Box display='flex' alignItems='center' gap={1}>
              <Typography variant='body1'  ml={2}>Activo:</Typography>
              <YesNoBadge
                value={extendedProps.isActive || false}
                yesColor={theme.palette.success.main}
                noColor={theme.palette.error.main}
              />
              <Typography variant='body2' color='text.secondary' ml={2}>
                Los campos marcados con <RequiredMark /> son obligatorios.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={6} display='flex' justifyContent='flex-end'>
            <CustomAlert
              dense
              severity='info'
              message='Las modificaciones aplicarán solo a nuevas órdenes.'
              maxWidth='75%'
              maxHeight={35}
              classes={{
                message: originAlertMessageClass,
                icon: originAlertIconClass
              }}
            />
          </Grid>
        </Grid>

        <Formik
          initialValues={extendedProps}
          onSubmit={async (values) => {
            await handleSetData(values)
          }}
          validationSchema={productOriginValidationSchema}
        >
          {({ isSubmitting, isValid, values }) => {
            const isExternalOrMixed =
              values.typeStorageId === eTypeStorage.Externo ||
              values.typeStorageId === eTypeStorage.Mixto
            const settingActive =
              settingsState.settings.find(
                (s) =>
                  s.settingsMetadataId ===
                  eSetting.PrioritizeByBrandAndCourierForExternalStorage
              )?.value === 'true'
            const isPrioritizationEnabled = isExternalOrMixed && settingActive

            return (
              <Form autoComplete='off' style={fillFormStyle}>
                <ModalScrollableContent
                  fill
                  actions={
                    <Fragment>
                      <Button
                        variant='outlined'
                        color='primary'
                        onClick={closeModal}
                        disabled={isSubmitting}
                      >
                        Cancelar
                      </Button>
                      <Button
                        disabled={isDisableButton(isSubmitting, isValid)}
                        type='submit'
                        variant='contained'
                        color='primary'
                        startIcon={
                          status === Status.Loading ? (
                            <CircularProgress size='0.9rem' />
                          ) : undefined
                        }
                      >
                        {status === Status.Loading ? '' : 'Guardar'}
                      </Button>
                    </Fragment>
                  }
                >
                <Grid container spacing={1}>
                  <Grid item xs={12} md={6}>
                    <Paper
                      variant='outlined'
                      style={{ padding: 8, marginBottom: 8 }}
                    >
                      <SectionTitle
                        icon={<DescriptionOutlined color='primary' />}
                      >
                        Información de origen
                      </SectionTitle>
                      <Grid container spacing={0.75}>
                        <Grid item xs={12}>
                          <LockedField label='Canal de venta (ClientAlias)'>
                            {renderAllRelations(
                              extendedProps.orderOriginNames || []
                            )}
                          </LockedField>
                        </Grid>
                        <Grid item xs={12}>
                          <LockedField label='Origen del producto'>
                            <Typography variant='body2' fontWeight={600}>
                              {extendedProps.origin}
                            </Typography>
                          </LockedField>
                          <Typography
                            variant='caption'
                            color='textSecondary'
                            display='block'
                          >
                            Identificador técnico utilizado por el WMS para
                            comunicarse con sistemas externos.
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <LockedField label='Referencia Externa (Marca PIM)'>
                            {renderAllRelations(
                              (
                                extendedProps.productOriginsExternalReferences ||
                                []
                              ).map((reference) => reference.name)
                            )}
                          </LockedField>
                          <Typography
                            variant='caption'
                            color='textSecondary'
                            display='block'
                          >
                            Identificador utilizado por PIM para relacionar con
                            la marca dentro del WMS.
                          </Typography>
                        </Grid>
                      </Grid>
                    </Paper>

                    <Paper
                      variant='outlined'
                      style={{ padding: 8, marginBottom: 8 }}
                    >
                      <SectionTitle icon={<SettingsOutlined color='primary' />}>
                        Configuración de Marca
                      </SectionTitle>
                      <Field
                        fullWidth
                        name='name'
                        component={TextField}
                        label={
                          <Fragment>
                            Nombre Marca en WMS
                            <RequiredMark />
                          </Fragment>
                        }
                        variant='outlined'
                      />
                    </Paper>

                    <Paper
                      style={{
                        padding: 8,
                        marginBottom: 8,
                        backgroundColor: '#eef4ff',
                        border: '1px solid #d6e4ff',
                        borderRadius: 8
                      }}
                    >
                      <SectionTitle icon={<InfoOutlined color='primary' />}>
                        Tolerancia en días de Picking FEFO
                      </SectionTitle>
                      <Field name='hasExpirationDays'>
                        {({ field }: any) => (
                          <FormControlLabel
                            control={
                              <Switch checked={field.value} {...field} />
                            }
                            label='Aplicar restricción FEFO'
                          />
                        )}
                      </Field>
                      <Collapse in={values.hasExpirationDays} unmountOnExit>
                        <Box mt={0.75}>
                          <Field
                            fullWidth
                            name='expirationDays'
                            component={TextField}
                            label={
                              <Fragment>
                                Días mínimos antes del vencimiento
                                <RequiredMark />
                              </Fragment>
                            }
                            variant='outlined'
                            type='number'
                            inputProps={{ min: 0 }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position='end'>
                                  días
                                </InputAdornment>
                              )
                            }}
                            onKeyDown={handleInputChangeNumericNotNegative}
                          />
                          <Typography
                            variant='caption'
                            color='textSecondary'
                            display='block'
                          >
                            Los productos con menos de la cantidad configurada
                            de días para vencer no podrán ser seleccionados
                            durante el picking.
                          </Typography>
                        </Box>
                      </Collapse>
                    </Paper>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Paper
                      variant='outlined'
                      style={{ padding: 8, marginBottom: 8 }}
                    >
                      <SectionTitle
                        icon={<Inventory2Outlined color='primary' />}
                      >
                        Tipo de almacenamiento
                        <RequiredMark />
                      </SectionTitle>
                      <Field name='typeStorageId'>
                        {({ field, meta }: any) => (
                          <Box mb={0.5}>
                            <RadioGroup
                              row
                              value={field.value}
                              onChange={(e) => {
                                const newValue = Number(e.target.value)
                                field.onChange({
                                  target: {
                                    name: 'typeStorageId',
                                    value: newValue
                                  }
                                })
                                requestGetTypeOperation(
                                  newValue,
                                  extendedProps.productOriginId
                                )
                              }}
                            >
                              <FormControlLabel
                                value={eTypeStorage.Interno}
                                control={<Radio />}
                                label='Interno'
                              />
                              <FormControlLabel
                                value={eTypeStorage.Externo}
                                control={<Radio />}
                                label='Externo'
                              />
                              <FormControlLabel
                                value={eTypeStorage.Mixto}
                                control={<Radio />}
                                label='Mixto'
                              />
                            </RadioGroup>
                            {meta.touched && meta.error && (
                              <div style={{ color: 'red' }}>{meta.error}</div>
                            )}
                          </Box>
                        )}
                      </Field>
                      <Typography
                        variant='caption'
                        color='textSecondary'
                        display='block'
                        mb={0.5}
                      >
                        Seleccione el tipo de almacenamiento para configurar los
                        almacenes por tipo de operación.
                      </Typography>
                      <Collapse in={values.typeStorageId > 0} unmountOnExit>
                        <Box
                          display='flex'
                          alignItems='center'
                          gap={1}
                          sx={{
                            bgcolor: '#eef4ff',
                            borderRadius: 1,
                            px: 1.25,
                            py: 0.5
                          }}
                        >
                          <WarehouseOutlined fontSize='small' color='primary' />
                          <Typography variant='subtitle2'>
                            Asignación de almacenes por tipo de operación
                          </Typography>
                        </Box>

                        <TypeOperationWarehousesTable
                          typeOperations={stateTypeOperation.data || []}
                          warehouses={warehouses || []}
                          isLoading={
                            stateTypeOperation.status === Status.Loading
                          }
                          selectedWarehousesByOperation={
                            selectedWarehousesByOperation || []
                          }
                          handleSelectedWarehouses={handleSelectedWarehouses}
                          showHeader={false}
                        />
                      </Collapse>
                    </Paper>

                    <Collapse in={isPrioritizationEnabled} unmountOnExit>
                      <Paper
                        variant='outlined'
                        style={{ padding: 8, marginBottom: 8 }}
                      >
                        <SectionTitle
                          icon={<PrecisionManufacturingIcon color='primary' />}
                        >
                          Configuración de priorización Modula
                        </SectionTitle>

                        <Grid container spacing={0.75}>
                          <Grid item xs={12}>
                            <Box
                              display='flex'
                              justifyContent='space-between'
                              alignItems='flex-start'
                            >
                              <Box display='flex' gap={1}>
                                <LocalShippingOutlined color='action' />
                                <Box>
                                  <Typography variant='body2' fontWeight={600}>
                                    Priorización Courier
                                  </Typography>
                                  <Typography
                                    variant='caption'
                                    color='textSecondary'
                                    display='block'
                                  >
                                    Prioriza los pedidos según courier y
                                    servicio.
                                  </Typography>
                                </Box>
                              </Box>
                              <Field name='hasPriorityCourier'>
                                {({ field }: any) => (
                                  <FormControlLabel
                                    labelPlacement='start'
                                    control={
                                      <Switch
                                        checked={field.value}
                                        {...field}
                                      />
                                    }
                                    label={field.value ? 'Activo' : 'Activar'}
                                  />
                                )}
                              </Field>
                            </Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Box
                              display='flex'
                              justifyContent='space-between'
                              alignItems='flex-start'
                            >
                              <Box display='flex' gap={1}>
                                <Inventory2Outlined color='action' />
                                <Box>
                                  <Typography variant='body2' fontWeight={600}>
                                    Priorización Monosku
                                  </Typography>
                                  <Typography
                                    variant='caption'
                                    color='textSecondary'
                                    display='block'
                                  >
                                    Otorga prioridad adicional a las órdenes con
                                    un solo SKU.
                                  </Typography>
                                </Box>
                              </Box>
                              <Field name='hasPrioritySingleSku'>
                                {({ field }: any) => (
                                  <FormControlLabel
                                    labelPlacement='start'
                                    control={
                                      <Switch
                                        checked={field.value}
                                        {...field}
                                      />
                                    }
                                    label={field.value ? 'Activo' : 'Activar'}
                                  />
                                )}
                              </Field>
                            </Box>
                          </Grid>

                          <Grid item xs={12}>
                            <Box
                              display='flex'
                              justifyContent='space-between'
                              alignItems='flex-start'
                            >
                              <Box display='flex' gap={1}>
                                <BalanceOutlined color='action' />
                                <Box>
                                  <Typography variant='body2' fontWeight={600}>
                                    Priorización por Sobrecarga de Pedidos
                                  </Typography>
                                  <Typography
                                    variant='caption'
                                    color='textSecondary'
                                    display='block'
                                  >
                                    Cuando los pedidos urgentes superen el
                                    porcentaje configurado, el sistema dará
                                    mayor oportunidad de procesamiento a pedidos
                                    de menor urgencia.
                                  </Typography>
                                </Box>
                              </Box>
                              <Field name='hasPriorityOverload'>
                                {({ field }: any) => (
                                  <FormControlLabel
                                    labelPlacement='start'
                                    control={
                                      <Switch
                                        checked={field.value}
                                        {...field}
                                      />
                                    }
                                    label={field.value ? 'Activo' : 'Activar'}
                                  />
                                )}
                              </Field>
                            </Box>

                            <Collapse
                              in={values.hasPriorityOverload}
                              unmountOnExit
                            >
                              <Box px={1} mt={0.5}>
                                <Typography
                                  variant='caption'
                                  color='textSecondary'
                                  display='block'
                                >
                                  Cuando los pedidos urgentes superen:
                                </Typography>
                                <Slider
                                  sx={{ py: 0.75 }}
                                  value={overloadPercentage}
                                  getAriaValueText={(value) => `${value}`}
                                  aria-labelledby='discrete-slider'
                                  valueLabelDisplay='on'
                                  step={10}
                                  min={0}
                                  max={100}
                                  onChange={(_, value) =>
                                    setOverloadPercentage(value as number)
                                  }
                                />
                                <Box
                                  display='flex'
                                  justifyContent='space-between'
                                >
                                  <Typography
                                    variant='caption'
                                    color='textSecondary'
                                    display='block'
                                  >
                                    0%
                                  </Typography>
                                  <Typography
                                    variant='caption'
                                    color='textSecondary'
                                    display='block'
                                  >
                                    100%
                                  </Typography>
                                </Box>
                              </Box>
                            </Collapse>
                          </Grid>
                        </Grid>
                      </Paper>
                    </Collapse>
                  </Grid>
                </Grid>
                </ModalScrollableContent>
              </Form>
            )
          }}
        </Formik>
      </Fragment>
    </Modal>
  )
}
